import * as React from "react";

/**
 * The arithmetic and the two hooks every chart in `components/charts/` shares:
 * how wide is the box, which index is the pointer over, where do the gridlines
 * go, and what path joins these points.
 *
 * Separate from the components on purpose - `ui/CLAUDE.md` rule 7. A module that
 * exports a component and a hook is not a Fast Refresh boundary, so editing it
 * remounts the module and mints fresh identities for anything it created at
 * module scope.
 */

/**
 * The chart's own width in CSS pixels, so an SVG can use a 1:1 viewBox.
 *
 * A percentage-width SVG with a fixed viewBox scales its *text* with the box,
 * which is how a chart ends up with 9px axis labels in a narrow column and 15px
 * ones in a wide card on the same page. Measuring instead means one coordinate
 * space per card, and every label the size the stylesheet asked for.
 *
 * Two details, and both are the difference between this working and flickering:
 *
 * **It returns 0 until it has measured**, and callers render a placeholder at
 * that width. A fallback width would be a lie for exactly one frame, and a
 * viewBox built from a lie is letterboxed by `preserveAspectRatio` - the whole
 * chart visibly snaps to size on mount.
 *
 * **It measures in `useLayoutEffect`, synchronously, before attaching the
 * observer.** `ResizeObserver` delivers its first entry asynchronously, so
 * relying on it alone would paint the placeholder once. Reading the box here
 * lets React re-render before the browser paints at all.
 */
export function useChartWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Rounded, so a sub-pixel layout shift does not re-render every chart on
    // the page for a change nobody can see.
    const measure = (next: number) => {
      if (next > 0) setWidth(Math.round(next));
    };

    measure(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => measure(entries[0]?.contentRect.width ?? 0));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

export interface Scale {
  /** The top of the axis - at or above the largest value, never below it. */
  max: number;
  /** Every gridline, from 0 upward. Includes both ends. */
  ticks: number[];
}

const LADDER = [1, 2, 5, 10];

/**
 * Gridlines on round numbers, with as little headroom as the ladder allows.
 *
 * Three tick counts are tried and the tightest top wins, rather than fixing on
 * four. Nine appointments against four ticks rounds to a step of 5 and an axis
 * that tops out at 20 - more than twice the data, so every bar renders in the
 * bottom half of the card. Five ticks gives a step of 2 and a top of 10.
 *
 * `integer` is on by default because these axes count things. A step of 2.5
 * labels the gridlines "2.5 appointments", which is not a quantity that exists.
 */
export function niceScale(
  rawMax: number,
  { integer = true, counts = [4, 5, 3] }: { integer?: boolean; counts?: number[] } = {}
): Scale {
  const target = Math.max(1, rawMax);
  let best: Scale | null = null;

  for (const count of counts) {
    const rough = target / count;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    let step = (LADDER.find((s) => rough <= s * magnitude) ?? 10) * magnitude;
    if (integer) step = Math.max(1, Math.round(step));

    const max = step * count;
    if (!best || max < best.max) {
      best = { max, ticks: Array.from({ length: count + 1 }, (_, i) => i * step) };
    }
  }

  return best!;
}

export interface Pt {
  x: number;
  y: number;
}

/**
 * One line through every reading.
 *
 * ── Nulls are skipped, not drawn and not broken around ─────────────────────
 *
 * A null is "there was nothing to measure in this period", which is not the
 * same fact as zero: a compliance rate over a day with no visits does not
 * exist, and plotting it on the floor would draw a quiet Sunday as a total
 * collapse. So an absent period contributes no vertex - **nothing is
 * interpolated at a date that measured nothing**, the crosshair finds no mark
 * there and the tooltip on it still reads "-" - and the line simply runs from
 * the reading before it to the reading after.
 *
 * It did not always. This file has carried four other answers to the same hole,
 * and they are worth one paragraph so that nobody spends another afternoon on
 * it: splitting the line at every null (at real volume no two readings are
 * adjacent, so every run was one point, every path a bare `M` that paints
 * nothing, and the card rendered blank with a tooltip full of correct figures);
 * a translucent hairline bridge (a scatter joined by cobwebs); stubs that
 * reached to within a few pixels and stopped (the area fill had to break at the
 * same place, which at 14 periods is a barcode); and a dashed hop per gap,
 * `connectNulls` style (correct, conventional, and more machinery than this
 * product wants).
 *
 * What is drawn now is one solid line and one continuous fill. The absence is
 * carried by the figures rather than by the stroke: the meters above the charts
 * render `-` on an empty window and never a zero, and each one publishes the
 * `sample` the rate was taken over.
 */
export function linePath(points: (Pt | null)[]): string {
  return polyline(readings(points));
}

/** Close a line back along the baseline, so it can be filled as an area. */
export function areaPath(_line: string, points: (Pt | null)[], baseline: number): string {
  const measured = readings(points);
  if (measured.length === 0) return "";

  const first = measured[0]!;
  const last = measured[measured.length - 1]!;

  return `${polyline(measured)} L${round(last.x)},${round(baseline)} L${round(first.x)},${round(baseline)} Z`;
}

const readings = (points: (Pt | null)[]): Pt[] =>
  points.filter((point): point is Pt => point != null);

const polyline = (run: Pt[]): string =>
  run.map((p, index) => `${index === 0 ? "M" : "L"}${round(p.x)},${round(p.y)}`).join(" ");

/**
 * Which points get an axis label: a constant stride, always including the
 * first and last, never closer together than one label's width.
 *
 * **This is the second version and the first two both had a real bug.** The
 * original labelled every nth point *and* always the last one, so a fortnight
 * at stride 3 drew day 12 and day 13 on top of each other at the right edge -
 * two dates overprinted. The fix for that rounded `count - 1` evenly-spaced
 * *positions* to the nearest index instead, which stopped the collision but
 * introduced a subtler fault: rounding error does not distribute evenly, so
 * the gaps between chosen indices alternated - 2, 1, 2, 2, 1, 2, 1, 2 - which
 * reads as arbitrary even though every date on it is real. A reader scanning
 * a date axis is pattern-matching on rhythm as much as on the numbers, and an
 * axis that skips one day, then two, then one, has no rhythm to find.
 *
 * The fix is to pick an actual **integer stride** - every 2nd point, every
 * 3rd - so every gap but possibly the last is identical by construction, and
 * then force the last point onto the axis regardless of where the stride
 * lands, dropping the second-to-last label if it would otherwise sit closer
 * to the last than the stride allows. One irregular gap at the very end reads
 * as "and today", which is the one place on this axis an irregularity means
 * something; irregularity in the middle just reads as noise.
 */
export function labelIndices(count: number, plotWidth: number, labelWidth = 52): number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];

  const fits = Math.max(2, Math.floor(plotWidth / labelWidth));
  if (count <= fits) return Array.from({ length: count }, (_, index) => index);

  const stride = Math.ceil((count - 1) / (fits - 1));
  const indices: number[] = [];
  for (let index = 0; index < count - 1; index += stride) indices.push(index);

  const last = count - 1;
  if (indices[indices.length - 1] !== last) {
    // Too close to the point before it: replace rather than add, so the axis
    // never shows two labels a stride apart plus one crowded in beside them.
    if (last - indices[indices.length - 1] < stride) indices.pop();
    indices.push(last);
  }

  return indices;
}

const round = (value: number) => Math.round(value * 10) / 10;

/**
 * Which index the pointer is over, remembering the last one while it fades out.
 *
 * Clearing the index on mouse-out would snap the tooltip back to the first point
 * on its way out, and the clamp is what stops a remembered index from a longer
 * range indexing past a now-shorter series - a crash rather than a glitch,
 * because every caller reads `points[index]` to build its rows.
 */
export function useHoverIndex(length: number): {
  index: number;
  hovering: boolean;
  set: (index: number | null) => void;
} {
  const [hover, setHover] = React.useState<number | null>(null);
  const last = React.useRef(0);

  if (hover != null) last.current = hover;
  const index = Math.max(0, Math.min(hover ?? last.current, length - 1));

  return { index, hovering: hover != null, set: setHover };
}
